/**
 * «Posso iscrivermi a questo torneo, e se no perche'?» — una risposta sola, derivata.
 *
 * 🔑 PERCHE' ESISTE. La scheda torneo aveva gia' tutti i pezzi — finestra d'iscrizione,
 * stato d'iscrizione, requisiti in passthrough — e li mostrava **crudi**, requisiti
 * compresi (un `JSON.stringify` in un `<pre>`). Ma la domanda che un utente si fa non e'
 * «quali sono i campi»: e' **«posso entrare?»**, e quella nessuno la rispondeva.
 *
 * ⚠️ E c'e' un motivo pratico oltre a quello di prodotto: il tentativo d'iscrizione fallisce
 * con `422 iscrizione.identity-unresolved` quando manca il **profilo di gioco**, e quel
 * rifiuto arriva **dopo** aver premuto. Sapere prima cosa manca vale piu' di un errore
 * spiegato bene dopo.
 *
 * 🔴 LA REGOLA CHE GOVERNA TUTTO IL FILE: **«non lo so» e' un verdetto, e va detto.** Se i
 * requisiti non sono arrivati, la risposta NON e' «idoneo» — e' `sconosciuto`. Dire di si'
 * su dati mancanti e' la stessa famiglia del vuoto affermato che questa sezione ha gia'
 * pagato una volta (`lib/listState.ts`): un'affermazione fatta al posto del server.
 */

import type { LimuloRegistrationState } from './model.ts';

/**
 * L'esito, in ordine di precedenza — e **l'ordine E' la logica**, non una preferenza:
 * a chi e' gia' iscritto non interessa che la finestra sia chiusa, e a chi non ha il
 * profilo non serve sapere che c'e' posto.
 */
export type LimuloEligibilita =
  | 'gia-iscritto'
  | 'sconosciuto'
  | 'non-pubblicato'
  | 'finestra-non-aperta'
  | 'finestra-chiusa'
  | 'manca-profilo-di-gioco'
  | 'pieno'
  | 'idoneo';

/**
 * Una copy per ogni verdetto, in UN posto solo.
 *
 * ⚠️ E' un `Record` COMPLETO di proposito: aggiungere un esito al verdetto senza dargli una
 * copy diventa un errore di compilazione, invece di una schermata muta scoperta da un utente.
 *
 * 📌 Sta qui e non nella schermata perche' da oggi lo leggono in DUE (la scheda torneo e il
 * pannello d'iscrizione): due mappe che rispondono alla stessa domanda sono due frasi che un
 * giorno dicono cose diverse per la stessa ragione.
 */
export const ELIGIBILITA_COPY: Record<LimuloEligibilita, string> = {
  'gia-iscritto': 'eligibility.alreadyIn',
  sconosciuto: 'eligibility.unknown',
  'non-pubblicato': 'eligibility.notPublished',
  'finestra-non-aperta': 'eligibility.notYet',
  'finestra-chiusa': 'eligibility.closed',
  'manca-profilo-di-gioco': 'eligibility.needsGameProfile',
  pieno: 'eligibility.full',
  idoneo: 'eligibility.eligible',
};

/**
 * L'ETICHETTA DELL'AZIONE — cosa dicono il titolo della riga e il bottone d'iscrizione.
 *
 * 🔴 Perche' esiste: fino al 15/09 dicevano «Iscriviti» SEMPRE, anche a chi era gia' dentro, e
 * fra i due compariva `eligibility.alreadyIn` («Ci sei — sei iscritto a questo torneo»). La
 * schermata si contraddiceva in tre righe, e l'ha segnalato Luca.
 *
 * ⚠️ E il motivo per cui e' una MAPPA sul verdetto e non un `disabled ? … : …`: il bottone e'
 * spento per sette verdetti su otto — pieno, chiuso, non pubblicato, serve-il-profilo… — e legare
 * l'etichetta allo spegnimento direbbe «Iscritto» a chi e' soltanto ESCLUSO. Sarebbe una bugia
 * peggiore di quella tolta: la prima confonde, la seconda afferma il falso.
 * ⇒ La condizione e' `gia-iscritto` e nient'altro. `Record` COMPLETO come sopra: un verdetto nuovo
 * senza etichetta e' un errore di compilazione, non un'etichetta ereditata per sbaglio.
 *
 * ⚠️ LA COPY «Iscritto» / «Registered» E' NOSTRA, NON DI LUCA: lui ha segnalato il difetto, non
 * dettato la parola. Cade senza discussione se ne vuole un'altra.
 */
export const ETICHETTA_AZIONE: Record<LimuloEligibilita, string> = {
  'gia-iscritto': 'register.alreadyDone',
  sconosciuto: 'register.do',
  'non-pubblicato': 'register.do',
  'finestra-non-aperta': 'register.do',
  'finestra-chiusa': 'register.do',
  'manca-profilo-di-gioco': 'register.do',
  pieno: 'register.do',
  idoneo: 'register.do',
};

/**
 * «Da questa barra si puo' ancora fare qualcosa?» — il caso in cui la risposta e' NO perche' il
 * torneo e' GIA' INIZIATO e tu non ci sei (L-2339, Luca 2026-09-27T11:34:58Z, opzione (a): «al
 * posto dei due bottoni spenti, una riga sola col motivo detto dal server; la barra resta»).
 *
 * 🔴 Prima c'erano «Iscriviti» e «Check-in» spenti, piu' la frase «Il check-in si fa dopo
 * l'iscrizione»: due comandi che non si possono premere e un consiglio che non si puo' seguire
 * (censimento del 26/09, riga 5). ⚠️ La barra NON sparisce — la regola di Luca del 06/09 «il
 * pannello non si nasconde mai» resta: cambia cosa c'e' dentro.
 *
 * Il motivo e' del SERVER, in due campi, e servono tutti e due:
 *  · `eligibilita === 'finestra-chiusa'`: il server dice iscrizioni chiuse (`registration.open:
 *    false`; dall'avvio in poi e' `window.state-closed`, `app-qtournaments/app/core/iscrizioni.js:
 *    289-290` @ `qlash-limulo@origin/staging` `fd57be9`);
 *  · `statoTorneo` fra gli stati pubblici DOPO l'avvio e non terminali (`in_progress`, `paused`;
 *    i terminali non montano la barra affatto, v. `LimuloTournamentDetail`).
 * ⛔ Non si deduce dal calendario, e un «non lo so» resta la barra di prima: `sconosciuto` non e'
 * `finestra-chiusa`, e uno stato assente o ignoto non e' «iniziato».
 * ⚠️ Se la barra ha ancora qualcosa da mostrare — il ritiro (chi e' dentro), un check-in da fare o
 * gia' fatto — non e' questo caso, e la barra resta com'e'.
 * ⚠️ PERIMETRO: la scelta di Luca e' sul torneo GIA' INIZIATO. Restano la barra di prima, con gli
 * stessi due bottoni spenti, tutti i casi «iscrizioni chiuse» di un torneo che il bordo dice
 * ancora `created`: la chiusura per DATA, quella manuale dell'organizzatore, `check_in_closed`,
 * `ready` e `blocked` (prima revisione di #468, nota). Non sono dimenticati: non sono stati scelti.
 */
const STATI_AVVIATI: ReadonlySet<string> = new Set(['in_progress', 'paused']);

/** Il torneo e' GIA' INIZIATO per lo stato pubblico che manda il bordo (`in_progress`, `paused`). */
export function torneoGiaAvviato(statoTorneo: unknown): boolean {
  return typeof statoTorneo === 'string' && STATI_AVVIATI.has(statoTorneo);
}

export function torneoAvviatoSenzaAzioni(input: {
  /** `tournament.status`, lo stato PUBBLICO che manda il bordo. */
  readonly statoTorneo: unknown;
  readonly eligibilita: LimuloEligibilita;
  /** La barra mostra il ritiro (anche spento): chi e' dentro ha un comando. */
  readonly ritiroPresente: boolean;
  /** Il check-in si puo' fare (`attivo`) o e' gia' fatto (`presente`). */
  readonly checkInDaMostrare: boolean;
}): boolean {
  if (input.eligibilita !== 'finestra-chiusa') return false;
  if (input.ritiroPresente || input.checkInDaMostrare) return false;
  return torneoGiaAvviato(input.statoTorneo);
}

/**
 * Una copy per ogni stato dichiarato dal server, in un posto solo — la leggono la card e la
 * scheda torneo. `Record` COMPLETO: uno stato nuovo senza copy e' un errore di compilazione.
 */
export const REG_STATE_COPY: Record<LimuloRegistrationState, string> = {
  open: 'window.open',
  scheduled: 'window.scheduled',
  closed: 'window.closed',
  full: 'window.full',
  'not-published': 'window.notPublished',
  unknown: 'window.unknown',
};

export interface IngressoEligibilita {
  /** `true` se il server dice che sei gia' dentro. */
  readonly registered: boolean;
  /**
   * 🔴 Lo stato d'iscrizione **secondo il server** (`model.ts:registrationStateOf`), non il
   * calendario. Il campo si chiamava `finestra` ed era il verdetto di `registrationWindow`:
   * il 2026-09-06 quella derivazione ha fatto mostrare «Iscrizioni: Aperte» su un torneo che
   * il server dichiarava `scheduled` nello stesso istante, perche' la finestra la decide
   * anche lo STATO del torneo e le date da sole non lo sanno. V. la nota estesa su
   * `registrationStateOf`.
   */
  readonly finestra: LimuloRegistrationState;
  /** I requisiti in passthrough, o `null` se non sono arrivati. */
  readonly requisiti: Record<string, unknown> | null;
  /** `true` se l'utente ha un profilo per il gioco di questo torneo. `null` = non lo sappiamo. */
  readonly haProfiloDiGioco: boolean | null;
}

/** `true` se i requisiti dichiarano che serve un profilo di gioco. */
export function serveProfiloDiGioco(requisiti: Record<string, unknown> | null): boolean {
  // ⚠️ SOLO `=== true`. Un valore assente, `null` o una stringa non significano «serve»:
  // significano «non l'ha detto», e trattarli come un si' bloccherebbe l'iscrizione con una
  // ragione inventata — che a schermo e' indistinguibile da una vera.
  return requisiti?.['gameProfileRequired'] === true;
}

/**
 * Il verdetto. L'ordine dei rami e' quello dichiarato sopra e non si riordina.
 */
export function eligibilitaDi(ingresso: IngressoEligibilita): LimuloEligibilita {
  // 1. Chi e' dentro e' dentro: nessun'altra condizione lo riguarda.
  if (ingresso.registered) return 'gia-iscritto';

  // 2. ⚠️ SUBITO DOPO, e prima di ogni giudizio: senza requisiti non si giudica. Questo
  //    ramo e' la ragione per cui esiste il verdetto `sconosciuto` — senza, ogni torneo con
  //    i requisiti non caricati sarebbe «idoneo», e l'utente lo scoprirebbe premendo.
  if (ingresso.requisiti === null) return 'sconosciuto';

  // 3. La finestra, COME LA DICHIARA IL SERVER. `unknown` non e' «aperta»: e' un'altra
  //    ignoranza dichiarata — e da oggi e' l'UNICA via per cui questa funzione puo' non
  //    sapere se si puo' entrare, perche' il calendario non ha piu' voce in capitolo.
  //    ⛔ Non rimettere qui `registrationWindow`: le date da sole non conoscono lo STATO del
  //    torneo, e su staging (06/09) hanno detto «aperte» su un torneo `scheduled`.
  if (ingresso.finestra === 'unknown') return 'sconosciuto';
  if (ingresso.finestra === 'not-published') return 'non-pubblicato';
  if (ingresso.finestra === 'scheduled') return 'finestra-non-aperta';
  if (ingresso.finestra === 'closed') return 'finestra-chiusa';
  // 🔑 `full` lo dice il SERVER, che conta le parti `active` col tetto vero
  //    (`core/iscrizioni.ts:358`). Prima qui c'era un confronto nostro fra `participantsCount`
  //    e `limits.maxPlayers`: e' stato TOLTO, non spostato. Con la finestra ormai in mano al
  //    server, un nostro «pieno» poteva contraddire un `open: true` appena letto — cioe' lo
  //    stesso difetto di prima a parti invertite, e su un dato che noi vediamo peggio di lui.
  if (ingresso.finestra === 'full') return 'pieno';

  // 4. Il prerequisito. `null` = non sappiamo se l'utente ha il profilo ⇒ non affermiamo.
  if (serveProfiloDiGioco(ingresso.requisiti)) {
    if (ingresso.haProfiloDiGioco === null) return 'sconosciuto';
    if (ingresso.haProfiloDiGioco === false) return 'manca-profilo-di-gioco';
  }

  return 'idoneo';
}
